import React, { useState } from 'react';
import { reviseFrameToVersion, canReviseAsset } from '../utils/nlRevise';

// A one-line natural-language "make a new version" control. Drop it on a photo
// card or in the lightbox: the user describes a change ("deepen the barrel,
// warmer sky, electric teal on the wave face") and we bake a NEW, non-destructive
// variation grouped with the original. The full editor is never required.
//
// Props:
//   asset    — the still being revised
//   compact  — tighter layout for the card footer
//   onDone   — (newId) => void, fired once the new version is queued
const QUICK = [
  'Warmer, more electric teal on the wave face',
  'Deepen the barrel without losing detail',
  'Clearer, less hazy sky',
  'More natural — pull back the saturation',
];

export default function RevisePrompt({ asset, compact = false, onDone, autoFocus = false }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  if (!canReviseAsset(asset)) return null;

  const run = async (note) => {
    const prompt = String(note ?? text).trim();
    if (!prompt || busy) return;
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const { reply } = await reviseFrameToVersion(asset, prompt);
      setMsg(reply || 'New version queued — it’ll appear shortly.');
      setText('');
      if (onDone) onDone();
    } catch (e) {
      setErr(e.message || 'Revision failed');
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); run(); }
  };

  return (
    <div className={`cs-revise${compact ? ' is-compact' : ''}`} onClick={(e) => e.stopPropagation()}>
      <div className="cs-revise-row">
        <input
          type="text"
          className="cs-revise-input"
          placeholder="Describe a change → new version…"
          value={text}
          disabled={busy}
          autoFocus={autoFocus}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
        />
        <button
          type="button"
          className="cs-btn cs-btn-primary cs-btn-sm"
          disabled={busy || !text.trim()}
          onClick={() => run()}
          title="Generate a new version from this note"
        >
          {busy ? '✨ Working…' : '✨ Version'}
        </button>
      </div>
      {!compact && !busy && !msg && (
        <div className="cs-revise-quick">
          {QUICK.map((q) => (
            <button key={q} type="button" className="cs-revise-chip" disabled={busy} onClick={() => run(q)}>
              {q}
            </button>
          ))}
        </div>
      )}
      {msg && <div className="cs-revise-note ok">✓ {msg}</div>}
      {err && <div className="cs-revise-note err">⚠ {err}</div>}
    </div>
  );
}
